import { useState } from 'react';

import { Button, Card } from '@/design-system';
import type { InvoiceFilters } from '@/entities/invoice';
import { ErrorState } from '@/shared/ui';

import { BatchResultCard } from '../components/BatchResultCard';
import { BillingRegion } from '../components/BillingRegion';
import { InvoicesCard } from '../components/InvoicesCard';
import { MonthCard } from '../components/MonthCard';
import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import { useBillingIntegration, useInvoices, useMonthSummary } from '../hooks';
import type { InvoiceBatch } from './invoice-batch';
import { TabColumn } from './TabColumn';

export interface InvoicesTabProps {
    /** Versturen is voorbehouden aan de eigenaar. */
    canSend: boolean;
    batch: InvoiceBatch;
}

/**
 * Facturen aan klanten: wat de klanten van het bedrijf aan het bedrijf betalen.
 * Boven de lijst de bevestiging en de uitkomst van "alles versturen", naast de
 * lijst de omzet van de maand.
 */
export function InvoicesTab({ canSend, batch }: InvoicesTabProps) {
    const [invoiceFilters, setInvoiceFilters] = useState<InvoiceFilters>({ page: 1 });
    const invoices = useInvoices(invoiceFilters);
    const integration = useBillingIntegration();
    const summary = useMonthSummary();
    const { draftItems, draftTotal, sendBatch } = batch;

    return (
        <TabColumn>
            {batch.confirming && draftItems.length > 0 ? (
                <Card
                    variant="warn"
                    padding="md"
                    role="group"
                    aria-label={billingCopy.sendAllConfirmLabel}
                >
                    <p className="m-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]">
                        {billingCopy.sendAllConfirm(draftItems.length)}
                        {draftTotal > draftItems.length
                            ? ` ${billingCopy.sendAllPartial(draftItems.length, draftTotal)}`
                            : ''}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={sendBatch.isPending}
                            onClick={() =>
                                batch.send(
                                    draftItems.map((invoice) => invoice.uuid),
                                    new Map(
                                        draftItems.map((invoice) => [invoice.uuid, invoice.label]),
                                    ),
                                )
                            }
                        >
                            {billingCopy.sendAllYes(draftItems.length)}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => batch.setConfirming(false)}
                        >
                            {billingCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : null}

            {sendBatch.data && !batch.dismissed ? (
                <BatchResultCard
                    outcome={sendBatch.data}
                    labels={batch.labels}
                    retrying={sendBatch.isPending}
                    onDismiss={() => batch.setDismissed(true)}
                    onRetry={(invoiceUuids) => batch.send(invoiceUuids, batch.labels)}
                />
            ) : null}

            {sendBatch.isError ? (
                <Card padding="md">
                    <ErrorState
                        error={new Error(describeBillingError(sendBatch.error))}
                        title={billingCopy.sendAllFailed}
                    />
                </Card>
            ) : null}

            {/* De lijst heeft de breedte nodig; de maand staat ernaast, en op een smal scherm eronder. */}
            <div className="grid items-start gap-[14px] @min-[960px]:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]">
                <div className="min-w-0">
                    <BillingRegion
                        title={billingCopy.invoicesTitle}
                        query={invoices}
                        skeletonHeight={180}
                    >
                        {invoices.data ? (
                            <InvoicesCard
                                page={invoices.data}
                                filters={invoiceFilters}
                                onFiltersChange={setInvoiceFilters}
                                busy={invoices.isPlaceholderData}
                                integration={integration.data ?? null}
                                canSend={canSend}
                            />
                        ) : null}
                    </BillingRegion>
                </div>

                <BillingRegion title={billingCopy.eyebrowLoading} query={summary}>
                    {summary.data ? <MonthCard summary={summary.data} /> : null}
                </BillingRegion>
            </div>
        </TabColumn>
    );
}
